import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { isStringNotEmpty, isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'

/** Query key: CM Environment ID (matches {@link AdminFilterValues.cme}). */
export const ADMIN_URL_QUERY_CME = 'cme'

/** Query key: Telephony server ID, or {@link ALL_VALUE} as `-1` for all servers in scope. */
export const ADMIN_URL_QUERY_TELEPHONY_SERVER = 'telephonyServer'

/** Query key: Partition ID within TelSys, or `-1` for all partitions. */
export const ADMIN_URL_QUERY_PARTITION = 'partition'

/**
 * Query key: partition GUID, carried alongside {@link ADMIN_URL_QUERY_PARTITION} on drill-through links.
 *
 * The v2 admin filters identify a partition by its ID within the telephony server, but the classic admin list
 * servlet's partition control is keyed by GUID, so a drill-through that may fall back to the classic list has to
 * carry both (APD-9658).
 */
export const ADMIN_URL_QUERY_PARTITION_GUID = 'partitionGuid'

/** Query key: include child CMEs when scoping lists (`true` / `false`). */
export const ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN = 'includeCmeChildren'

/** Query key: search text (matches admin filter search). */
export const ADMIN_URL_QUERY_SEARCH = 'search'

/**
 * Parsed admin list URL query parameters used to initialise {@link AdminFilters} after options load.
 */
export type AdminListUrlBootstrap = {
    readonly cmeId?: string
    readonly telephonyServer?: string
    readonly partition?: string
    readonly includeCmeChildren?: boolean
    readonly search?: string
}

/**
 * Reads admin list deep-link query parameters from the current location search string.
 *
 * @param searchParams - URL search params (e.g. from {@link useSearchParams}).
 * @returns Bootstrap values to apply to admin filters; omits keys that are absent or empty.
 */
export function parseAdminListUrlSearchParams(searchParams: URLSearchParams): AdminListUrlBootstrap {
    const cmeRaw: Nullable<string> = searchParams.get(ADMIN_URL_QUERY_CME)
    const telRaw: Nullable<string> = searchParams.get(ADMIN_URL_QUERY_TELEPHONY_SERVER)
    const partRaw: Nullable<string> = searchParams.get(ADMIN_URL_QUERY_PARTITION)
    const includeRaw: Nullable<string> = searchParams.get(ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN)
    const searchRaw: Nullable<string> = searchParams.get(ADMIN_URL_QUERY_SEARCH)

    let includeCmeChildren: Definable<boolean>
    if (includeRaw === 'true') {
        includeCmeChildren = true
    } else if (includeRaw === 'false') {
        includeCmeChildren = false
    }

    const bootstrap: AdminListUrlBootstrap = {
        ...(isStringNotEmpty(cmeRaw ?? EMPTY_STRING) ? { cmeId: cmeRaw as string } : {}),
        ...(isStringNotEmpty(telRaw ?? EMPTY_STRING) ? { telephonyServer: telRaw as string } : {}),
        ...(isStringNotEmpty(partRaw ?? EMPTY_STRING) ? { partition: partRaw as string } : {}),
        ...(isVarNotEmpty(includeCmeChildren) ? { includeCmeChildren } : {}),
        ...(isStringNotEmpty(searchRaw ?? EMPTY_STRING) ? { search: searchRaw as string } : {})
    }

    return bootstrap
}

/**
 * Builds a path with query string for admin list deep links. Omits undefined or empty values.
 *
 * @param routePath - Path under the app router (e.g. `/telsys`).
 * @param query - Query keys and string values to append.
 * @returns Path including `?` query when non-empty.
 */
export function buildAdminListPathWithQuery(routePath: string, query: Record<string, Definable<string>>): string {
    const params: URLSearchParams = new URLSearchParams()

    Object.entries(query).forEach(([key, value]: [string, Definable<string>]): void => {
        if (isVarNotEmpty(value) && value !== EMPTY_STRING) {
            params.set(key, value)
        }
    })

    const serialized: string = params.toString()

    return serialized.length > 0 ? `${routePath}?${serialized}` : routePath
}
